import { Api } from '@/core/api.js'
import store from '@/store'
import i18n from '@/i18n'
import router from '@/router'
import { db_issues, db_issues_templates, db_tasks, db_templates, db_timers, db_checks_to_sync, db_login, db_updates, db_employees, db_roles, db_categories, db_products, db_collections, db_printlogs, db_error_logs, db_library_categories, db_sections, db_files, db_highlight_files, db_logbook, db_legal_conditions, dbEmployeeLoginLogs, db_printlogs_android, db_languages, db_locations, db_tasks_queue, db_registers_queue, db_registers_items, db_audits_items, db_audits_queue, db_issues_items, db_issues_queue, db_registers, db_screenlock, cleanAllStorage } from '@/database'
import Vue from 'vue'
import { TOOLS, REFINER_SESSION_TRACKED_KEY } from '@/constants'
import { USER_PREFERENCES_STORAGE_KEY } from '@/store/modules/userPreferences'
import { finishWithSsoLogoutRedirect, isSsoLogoutRedirect, navigateToLoginAfterLogout } from '@/utils/ssoLogout'

const api = new Api()

function normalizeTrialFromApi(data) {
    if (!data || typeof data !== 'object') {
        return { expirationDate: '', showTrialBanner: false }
    }

    return {
        expirationDate: data.trial_expiration_date || '',
        showTrialBanner: data.show_trial_banner === true
    }
}

// initial state
const state = {
    location: {},
    account: -1,
    config: {},
    printer: {},
    hasLogin: false,
    conditionsVersion: -1,
    app_android: '',
    app_icon: '',
    token: '',
    roles: {},
    loadLocalForage: false,
    releases: {},
    allReleases: {},
    blackListData: {
        warning: false,
        blocked: false
    },
    screenlock: {
        checklist_id: false,
        message: ''
    },
    wrong_ip: false,
    trial_end_date: '',
    show_trial_banner: false,
    showTrialEndScreen: false,
    trial_days_left: 0,
    trial_message: null,
    removeRolesLevel: false,
    auditor: false,
    isLoadedLocation: false,
    auditorLocalSeleceted: false,
    kitchenModeFeatureFlag: null, // KITCHEN MODE FEATURE FLAG
    radarFlag: 0, // default value is 0. value is 1 if the radar tool is enabled
    npsBlocked: false
}

// getters
const getters = {
    getHasLogin: (state, getters, rootState) => {
        return state.hasLogin
    },
    getConfig: (state, getters, rootState) => {
        return state.config
    },
    getConditionsVersion: async (state, getters, rootState) => {
        return state.conditionsVersion != -1 ? state.conditionsVersion : await db_login.getItem('conditions_version')
    },
    getToken: (state, getters, rootState) => {
        return state.token
    },
    getAppAndroid: (state, getters, rootState) => {
        return state.app_android
    },
    getRoles: (state, getters, rootState) => {
        return state.roles
    },
    getLocation: (state, getters, rootState) => {
        return state.location
    },
    // Account ID getter
    getAccount: (state, getters, rootState) => {
        return state.account
    },
    getPrinter: (state, getters, rootState) => {
        return state.printer
    },
    isLoad: (state, getters, rootState) => {
        return state.loadLocalForage
    },
    getCPUrl: (state, getters, rootState) => {
        return process.env.VUE_APP_CP_URL
    },
    getAPIurl: (state, getters, rootState) => {
        return process.env.VUE_APP_API_URL
    },
    getReleases: (state, getters, rootState) => (id) => {
        if (id) {
            return state.releases[id]
        } else {
            const items = state.releases
            const sortedItems = Object.values(items).sort((a, b) => {
                const date1 = moment(a.created_date * 1000)
                const date2 = moment(b.created_date * 1000)
                if (date1 > date2) return 1
                if (date1 < date2) return -1
                return 0
            })
            return sortedItems
        }
    },
    getAllReleases: (state, getters, rootState) => (id) => {
        if (id) {
            return state.allReleases[id]
        } else {
            const items = state.allReleases
            if (items) {
                const sortedItems = Object.values(items).sort((a, b) => {
                    const date1 = moment(a.created_date * 1000)
                    const date2 = moment(b.created_date * 1000)
                    if (date1 > date2) return 1
                    if (date1 < date2) return -1
                    return 0
                })
                return sortedItems
            }

            return []
        }
    },
    getBlackListData: (state, getters, rootState) => {
        return state.blackListData
    },
    getScreenlockData: (state, getters, rootState) => {
        return state.screenlock
    },
    getWrongIp: (state, getters, rootState) => {
        return state.wrong_ip
    },
    getTrialEndDate: (state, getters, rootState) => {
        return state.trial_end_date
    },
    getShowTrialBanner: (state, getters, rootState) => {
        return Boolean(state.show_trial_banner)
    },
    getShowTrialEndScreen: (state, getters, rootState) => {
        return state.showTrialEndScreen
    },
    getTrialDaysLeft: (state, getters, rootState) => {
        return state.trial_days_left
    },
    getRemoveRolesLevel: (state, getters, rootState) => {
        return state.removeRolesLevel
    },
    getTrialMessage: (state, getters, rootState) => {
        return state.trial_message
    },
    getIsLoadedLocation: (state, getters, rootState) => {
        const location = state.location
        if (location && Object.keys(location).length > 0) {
            return true
        }

        return state.isLoadedLocation
    },
    getAuditorLocalSeleceted: (state, getters, rootState) => {
        return state.auditorLocalSeleceted
    },
    getKitchenModeFeatureFlag: (state) => {
        return state.kitchenModeFeatureFlag
    },
    isNpsBlocked: (state) => {
        return state.npsBlocked === true
    },
    /** Códigos numéricos de las herramientas habilitadas para la tienda loggeada (account.config.tools). */
    getEnabledToolCodes: (state) => {
        if (!state.config || !Array.isArray(state.config.tools)) {
            return []
        }

        return state.config.tools.map((tool) => Number(tool))
    },
    getPlsModeFeatureFlag: (state) => {
        if (!state.config || !state.config.tools || !Array.isArray(state.config.tools)) {
            return false
        }
        const tools = state.config.tools.map((t) => Number(t))
        return tools.includes(18) || tools.includes(20)
    },
    getLocationHasActionPlansTool: (state, getters) => {
        return getters.getEnabledToolCodes.includes(Number(TOOLS.ACTION_PLANS))
    },
    getRadarFlag: (state, getters, rootState) => {
        return state.radarFlag
    }
}

// actions
const actions = {
    async doLogin(context, params) {
        // // var overlay = this._vm.$overlay
        // overlay.loading()
        context.commit('logout')
        await context.dispatch('employee/resetState', {}, { root: true })
        context.rootState.refresh.employee = true

        const appVersion = process.env.VUE_APP_VERSION
        const platform = process.env.VUE_APP_IS_APP == 'TRUE' ? 1 : 2
        const device =
            process.env.VUE_APP_IS_APP == 'TRUE'
                ? {
                      manufacture: window.device.manufacture,
                      model: window.device.model,
                      plaftorm: window.device.platform,
                      version: window.device.version,
                      current_time: moment().format('YYYY-MM-DD HH:mm:ss')
                  }
                : {
                      platform: window.navigator.platform,
                      userAgent: window.navigator.userAgent,
                      version: window.navigator.appVersion,
                      current_time: moment().format('YYYY-MM-DD HH:mm:ss')
                  }
        const device_uuid = context.rootGetters.getUuid
        const coords = context.rootGetters.getGeoLocation

        return api
            .login('login', {
                username: params.username,
                password: params.password,
                version: appVersion,
                platform: platform,
                device: JSON.stringify(device),
                uuid: device_uuid,
                geolocation: coords ? coords.latitude + ',' + coords.longitude : null,
                mq: store.getters['getMediaQuery']
            })
            .then(async function (response) {
                // store.dispatch( 'checkChangesPrint', response.data.printer );

                if (response.status) {
                    try {
                        await db_locations.setItem(params.username.toLowerCase(), {
                            account_name: response.data.account_name,
                            account_logo: response.data.avatar,
                            location_name: response.data.name,
                            password: window.btoa(params.password),
                            city: response.data.city,
                            state: response.data.state,
                            address: response.data.address,
                            cp: response.data.cp,
                            lang: response.data.lang
                        })
                    } catch (error) {
                        console.error('ERROR SETTING LOCATION', error)
                    }

                    // KITCHEN MODE FEATURE FLAG
                    if (response.data && response.data.config && response.data.config.kitchen_mode) {
                        context.commit('setKitchenModeFeatureFlag', response.data.config.kitchen_mode)
                    }

                    if (typeof response.data.current_time !== 'undefined' && response.data.current_time) {
                        const currentTime = moment(moment(response.data.current_time).format('YYYY-MM-DD HH:mm:ss'))
                        const now = moment(moment().format('YYYY-MM-DD HH:mm:ss'))
                        const difference = Math.round(currentTime.diff(now, 'minutes') / 60) * 60
                        context.commit('setServerTimeDifference', difference, { root: true })
                    }

                    context.commit('setTimeZoneName', response.data.timezone_name, { root: true })

                    context.commit('setVersionCurrent', response.data.version_current, { root: true })
                    context.commit('setVersionMin', response.data.version_min, { root: true })
                    context.commit('setVersionRecommended', response.data.version_recommended, { root: true })

                    //   store.dispatch("checkChangesPrint", response.data.printer);
                    context.dispatch('loadData', response.data)
                    context.dispatch('loadAppIcon', { path: response.data.app_icon, lastUpdate: response.data.app_icon_last_update * 1000 }, { root: true })
                    context.dispatch('loadExternalTools', response.data.menu_items, { root: true })
                    context.dispatch('pusher/init', {}, { root: true })
                    var printer = Object.assign(response.data.printer)
                    log('SETTING PRINTER', printer)
                    // context.commit('printer/setPrinterData', printer, { root: true })
                    context.dispatch('printer/savePrinterData', printer, { root: true })

                    if (response.data.config && response.data.config.home) {
                        context.commit('setCustomHome', response.data.config.home, { root: true })
                    } else {
                        context.commit('setCustomHome', false, { root: true })
                    }

                    // free labelling
                    const freeLabelling = response.data.config && typeof response.data.config.freeLabelling !== 'undefined' && response.data.config.freeLabelling ? 1 : 0
                    context.commit('setFreeLabelling', freeLabelling, { root: true })

                    const pastLabelling = response.data.config && typeof response.data.config.pastLabelling !== 'undefined' && response.data.config.pastLabelling ? 1 : 0
                    context.commit('setPastLabelling', pastLabelling, { root: true })

                    const sensorLimitation = response.data.config && typeof response.data.config.sensorLimitation !== 'undefined' && response.data.config.sensorLimitation ? response.data.config.sensorLimitation : 0
                    context.commit('setSensorLimitation', sensorLimitation, { root: true })

                    const relatedLabels = response.data.config && typeof response.data.config.relatedLabels !== 'undefined' && response.data.config.relatedLabels ? response.data.config.relatedLabels : 0
                    context.commit('setRelatedLabels', relatedLabels, { root: true })

                    const dateLimitation = response.data.config && typeof response.data.config.dateLimitation !== 'undefined' && response.data.config.dateLimitation ? response.data.config.dateLimitation : 0
                    context.commit('setDateLimitation', dateLimitation, { root: true })

                    const canCreateUsers = response.data.config && typeof response.data.config.canCreateUsers !== 'undefined' ? response.data.config.canCreateUsers : 1
                    context.commit('setCanCreateUsers', canCreateUsers, { root: true })

                    const canAccessTemplates = response.data.config && typeof response.data.config.canAccessTemplates !== 'undefined' ? response.data.config.canAccessTemplates : 1
                    context.commit('setCanAccessTemplates', canAccessTemplates, { root: true })

                    const maxLabelsPrint = response.data.config && typeof response.data.config.maxLabelsPrint !== 'undefined' && response.data.config.maxLabelsPrint ? response.data.config.maxLabelsPrint : 100
                    context.commit('setMaxLabelsPrint', maxLabelsPrint, { root: true })

                    const callLoginUsers = response.data.config && typeof response.data.config.callLoginUsers !== 'undefined' ? response.data.config.callLoginUsers : 0
                    context.commit('setCallLoginUsers', callLoginUsers, { root: true })

                    const timeout = response.data.config && typeof response.data.config.timeout !== 'undefined' && response.data.config.timeout ? parseInt(response.data.config.timeout) * 60 : idle_time
                    context.commit('setTimeout', timeout, { root: true })

                    const license_end_date = response.data && typeof response.data.license_end_date !== 'undefined' && response.data.license_end_date ? response.data.license_end_date : false

                    context.commit('setLicenseEndDate', license_end_date, { root: true })

                    const note_img = response.data.config && typeof response.data.config.note_img !== 'undefined' && response.data.config.note_img ? response.data.config.note_img : 'all'
                    context.commit('setNoteImg', note_img, { root: true })

                    const screenlockFlag = response.data.config && typeof response.data.config.screenlock !== 'undefined' ? response.data.config.screenlock : 0
                    context.commit('setScreenlockFlag', screenlockFlag, { root: true })

                    const offline = response.data.offline ? response.data.offline : 0
                    context.commit('setOffline', offline, { root: true })

                    const tp_round = response.data.config && response.data.config.tp_round && parseInt(response.data.config.tp_round) != 0 ? parseInt(response.data.config.tp_round) : false
                    context.commit('setTpRound', tp_round, { root: true })

                    const channels = response.data.config && response.data.config.channels && parseInt(response.data.config.channels) != 0 ? parseInt(response.data.config.channels) : false
                    context.commit('setLocationChannels', channels, { root: true })

                    const canReprintLabel = response.data.config && typeof response.data.config.canReprintLabel !== 'undefined' && parseInt(response.data.config.canReprintLabel) >= 0 ? response.data.config.canReprintLabel : 1
                    context.commit('setCanReprintLabel', canReprintLabel, { root: true })

                    const sensorBT = response.data.config && typeof response.data.config.sensor_bt !== 'undefined' && response.data.config.sensor_bt ? response.data.config.sensor_bt : false
                    context.commit('setSensorBluetoothConnection', sensorBT, { root: true })

                    const sentryLogs = response.data.config && typeof response.data.config.SENTRY_LOGS !== 'undefined' ? response.data.config.SENTRY_LOGS : false
                    context.commit('setSentryLogs', sentryLogs, { root: true })

                    const popupTextInput = response.data.config && typeof response.data.config.popupTextInput !== 'undefined' ? response.data.config.popupTextInput : false
                    context.commit('setPopupTextInput', popupTextInput, { root: true })

                    const popupTextInputHeight = response.data.config && typeof response.data.config.popupTextInputHeight !== 'undefined' ? response.data.config.popupTextInputHeight : 600
                    context.commit('setPopupTextInputHeight', popupTextInputHeight, { root: true })

                    const intlCurrency = response.data.config && typeof response.data.config.INTL_CURRENCY !== 'undefined' ? response.data.config.INTL_CURRENCY : 'EUR'
                    context.commit('intl/setCurrency', intlCurrency, { root: true })
                    const intlLocale = response.data.config && typeof response.data.config.INTL_LOCALE !== 'undefined' ? response.data.config.INTL_LOCALE : 'es-ES'
                    context.commit('intl/setLocale', intlLocale, { root: true })

                    const radarFlag = response.data && response.data.config && typeof response.data.config.radar !== 'undefined' ? response.data.config.radar : 0

                    context.commit('setRadarFlag', radarFlag)

                    const gcAdvanceForecastFlag = response.data && response.data.config && typeof response.data.config.gcAdvanceForecast !== 'undefined' ? response.data.config.gcAdvanceForecast : 0

                    context.commit('setGcAdvanceForecastFlag', gcAdvanceForecastFlag, { root: true })

                    context.dispatch('extractConfigOptions', response.data.config)

                    context.commit(
                        'loginUser/setLocation',
                        {
                            account_name: response.data.account_name,
                            id: response.data.id,
                            name: response.data.name,
                            avatar: response.data.avatar,
                            identification: response.data.identification,
                            username: response.data.username,
                            timezone: response.data.timezone,
                            email: response.data.email,
                            token: response.data.token,
                            city: response.data.city,
                            state: response.data.state,
                            address: response.data.address,
                            cp: response.data.cp
                        },
                        { root: true }
                    )

                    context.commit('loginUser/setAccount', response.data.account_id, { root: true })

                    context.commit('setReleases', response.data.releases)

                    context.commit('login', response)

                    const location = {
                        id: response.data.id,
                        name: response.data.name,
                        avatar: response.data.avatar,
                        identification: response.data.identification,
                        username: response.data.username,
                        timezone: response.data.timezone,
                        email: response.data.email,
                        token: response.data.token
                    }

                    await db_login.setItem('token', response.data.token)
                    await db_login.setItem('location', location)
                    await db_login.setItem('removeRolesLevel', response.data.removeRolesLevel)
                    await db_login.setItem('account', response.data.account_id)
                    await db_login.setItem('config', response.data.config)
                    // await db_login.setItem('printer', dt.printer)
                    await db_login.setItem('app_android', response.data.app_android)
                    await db_login.setItem('app_icon', response.data.app_icon)
                    await db_login.setItem('alex_info_url', response.data.alex_info_url)
                    await db_login.setItem('roles', response.data.roles)
                    await db_login.setItem('conditions_version', response.data.conditions_version)

                    context.dispatch('loadTimeZonesAndLanguages', {})

                    context.dispatch('applyTrialFromApi', response.data)
                    context.commit('setTrialMessage', response.data.trial_message)

                    context.commit('setWrongIp', false)
                    context.commit('setisLoaded', true)
                    context.commit('setIsLoadedLocation', true)

                    // Check if the timezone difference and device time are different
                    context.dispatch('checkDateTimezoneDifference', { root: true })

                    //   context.commit('employee/setRoles',response.data.roles,{root: true})
                    if (context.rootGetters.getIsAndroid && FirebasePlugin) {
                        FirebasePlugin.setUserId(response.data.id)
                        FirebasePlugin.setUserProperty('account', response.data.account_id)
                        FirebasePlugin.setUserProperty('location_name', response.data.location_name)
                        FirebasePlugin.setCrashlyticsUserId(response.data.id)
                    }
                } else {
                    if (response.blackListWarning) {
                        context.commit('setBlacklistData', { warning: true, blocked: false })
                    }
                    if (response.blackListBlocked) {
                        context.commit('setBlacklistData', { warning: false, blocked: true })
                    }
                    if (response.wrong_ip) {
                        context.commit('setWrongIp', true)
                    } else {
                        context.commit('setWrongIp', false)
                    }
                    return false
                }
            })
            .then(function () {
                if (context.state.hasLogin) {
                    return true
                } else {
                    return false
                }
            })
    },

    async doLoginAuditor(context, params) {
        context.commit('logout')

        const appVersion = process.env.VUE_APP_VERSION
        const platform = process.env.VUE_APP_IS_APP == 'TRUE' ? 1 : 2
        const device =
            process.env.VUE_APP_IS_APP == 'TRUE'
                ? {
                      manufacture: window.device.manufacture,
                      model: window.device.model,
                      plaftorm: window.device.platform,
                      version: window.device.version
                  }
                : {}
        return api
            .login('login/auditor', {
                email: params.email,
                password: params.password,
                version: appVersion,
                platform: platform,
                device: JSON.stringify(device)
            })
            .then(async function (response) {
                await cleanAllStorage()
                if (response.status) {
                    // Locations
                    if (response.data.locations) {
                        const locations = []
                        for (let location of response.data.locations) {
                            locations.push({
                                account_name: location.account_name,
                                account_logo: location.account_logo,
                                location_name: location.location_name,
                                city: location.city,
                                state: location.state,
                                address: location.address,
                                cp: location.cp,
                                lang: location.lang,
                                location_id: location.location_id,
                                username: location.username
                            })
                        }

                        context.commit('loginUser/setLocations', locations, { root: true })
                    }

                    context.commit('login/setToken', response.token, { root: true })
                    const auditor = { ...response.data }
                    delete auditor.locations
                    context.commit('loginUser/setAuditor', auditor, { root: true })
                    context.commit('setAuditorLogin', auditor, { root: true })
                    const timeout = response.data.config && typeof response.data.config.timeout !== 'undefined' && response.data.config.timeout ? parseInt(response.data.config.timeout) * 60 : idle_time
                    context.commit('setTimeout', timeout, { root: true })

                    if (response.data.config) {
                        const config = JSON.parse(JSON.stringify(response.data.config))
                        context.commit('setConfig', config)
                        if (config.home) {
                            context.commit('setCustomHome', config.home, { root: true })
                        }
                    } else {
                        context.commit('setCustomHome', false, { root: true })
                    }

                    return true
                } else {
                    return false
                }
            })
            .catch((err) => {
                logError(err)
                return false
            })
    },

    async doLoadLocalForage() {
        await initLocalForage()
    },

    async initLocation(context, params = {}) {
        // this._vm.$overlay.loading()
        params = params || {}

        const isOnline = fnCheckConnection()

        await initLocalForage(!isOnline)
        let last_action = context.rootGetters.getLastEmployeeAction
        let employee_id = store.getters['loginUser/getLocalEmployee']

        const currentPath = router.history && router.history.current ? router.history.current.path : ''
        const isPlsStockRoute = currentPath && (currentPath.includes('stock-pls') || currentPath.includes('phu-production'))
        const isPlsRoute = currentPath && (currentPath === '/pls' || currentPath.startsWith('/pls/'))

        if (!params.skipIdleLogout && !isPlsStockRoute && !isPlsRoute && last_action && last_action.time < moment().valueOf() - context.rootGetters.getTimeout * 1000 && employee_id) {
            context
                .dispatch(
                    'loginUser/doLogout',
                    {
                        employee: employee_id
                    },
                    { root: true }
                )
                .then(function (response) {
                    // router.push(mixin.redirectPath('/home')).catch(err => {
                    //     if (err.name == 'NavigationDuplicated') {
                    //         router.go(router.history.current)
                    //     }
                    // })
                })
        }

        if (isOnline) {
            const device =
                process.env.VUE_APP_IS_APP == 'TRUE'
                    ? {
                          manufacture: window.device.manufacture,
                          model: window.device.model,
                          plaftorm: window.device.platform,
                          version: window.device.version,
                          current_time: moment().format('YYYY-MM-DD HH:mm:ss')
                      }
                    : {
                          platform: window.navigator.platform,
                          userAgent: window.navigator.userAgent,
                          version: window.navigator.appVersion,
                          current_time: moment().format('YYYY-MM-DD HH:mm:ss')
                      }
            return api
                .getSession({
                    mq: store.getters['getMediaQuery'],
                    auditor: store.getters['loginUser/getAuditor'],
                    device: JSON.stringify(device)
                })
                .then(async function (response) {
                    if (response && (!response.status || response.status == 'false')) {
                        console.error(response)
                        context.commit('setNpsBlocked', false)
                        if (response.wrong_ip) {
                            context.commit('setWrongIp', true)
                        } else {
                            context.commit('setWrongIp', false)
                            let location_info = await db_locations.getItem(state.location.username)
                            context.commit(
                                'loginUser/setLocation',
                                {
                                    id: state.location.id,
                                    name: state.location.name,
                                    account_name: location_info.account_name,
                                    avatar: state.location.avatar,
                                    identification: state.location.identification,
                                    username: state.location.username,
                                    timezone: state.location.timezone,
                                    email: state.location.email,
                                    token: state.location.token,
                                    city: location_info.city,
                                    state: location_info.state,
                                    address: location_info.address,
                                    cp: location_info.cp
                                },
                                { root: true }
                            )
                            const offlineLang = (location_info.lang || 'es_ES').split('_')[0]
                            store.commit('changeLanguage', offlineLang)
                            return context.commit('setLogin')
                        }
                    }

                    if (response && response.data) {
                        // Saltar esto si es auditor
                        if (response.data.passwd && response.data.username && !response.data.auditor) {
                            // Obtener datos de la base de datos local con la key del usuario
                            try {
                                let location_info = await db_locations.getItem(response.data.username.toLowerCase())
                                if (location_info.password != window.btoa(response.data.passwd)) {
                                    // Logout
                                    context.dispatch('logout').then(function (result) {
                                        if (isSsoLogoutRedirect(result)) {
                                            return
                                        }
                                        router.push({ name: 'Login' }).then(() => {
                                            window.location.reload()
                                        })
                                    })
                                }
                            } catch (error) {
                                logError('ERROR SETTING LOCATION IN INIT', error)
                            }
                        }

                        if (response.data.config.api) {
                            localStorage.setItem('api', response.data.config.api)
                        } else {
                            if (localStorage.getItem('api')) {
                                localStorage.removeItem('api')
                            }
                        }

                        context.commit('setWrongIp', false)

                        if (typeof response.data.current_time !== 'undefined' && response.data.current_time) {
                            const currentTime = moment(moment(response.data.current_time).format('YYYY-MM-DD HH:mm'))
                            const now = moment(moment().format('YYYY-MM-DD HH:mm'))
                            const difference = Math.round(currentTime.diff(now, 'minutes') / 60) * 60
                            context.commit('setServerTimeDifference', difference, { root: true })
                        }

                        // KITCHEN MODE FEATURE FLAG
                        if (response.data && response.data.config && response.data.config.kitchen_mode) {
                            context.commit('setKitchenModeFeatureFlag', response.data.config.kitchen_mode)
                        }

                        context.commit('setVersionCurrent', response.data.version_current, { root: true })
                        context.commit('setVersionMin', response.data.version_min, { root: true })
                        context.commit('setVersionRecommended', response.data.version_recommended, { root: true })
                        context.commit('setChatActive', response.data.chat, { root: true })

                        context.commit('setTimeZoneName', response.data.timezone_name, { root: true })

                        context.dispatch('applyTrialFromApi', response.data)
                        context.commit('setTrialMessage', response.data.trial_message)

                        const radarFlag = response.data.config && typeof response.data.config.radar !== 'undefined' ? response.data.config.radar : 0

                        context.commit('setRadarFlag', radarFlag)

                        context.commit('setisLoaded', true)
                        context.dispatch('loadData', response.data)
                        context.dispatch('loadAppIcon', { path: response.data.app_icon, lastUpdate: response.data.app_icon_last_update * 1000 }, { root: true })
                        context.dispatch('loadExternalTools', response.data.menu_items, { root: true })
                        context.dispatch('pusher/init', {}, { root: true })
                        //   context.commit('employee/setRoles',response.data.roles,{root: true})
                        var printer = Object.assign({}, response.data.printer)
                        // context.commit('printer/setPrinterData', printer, { root: true })
                        context.dispatch('printer/savePrinterData', printer, { root: true }).then(() => {
                            log('FINISH SET PRINTER INFO')
                        })

                        if (response.data.config && response.data.config.home) {
                            context.commit('setCustomHome', response.data.config.home, { root: true })
                        } else {
                            context.commit('setCustomHome', false, { root: true })
                        }

                        if (response.data.config && response.data.config.screenlock && context.rootGetters['loginUser/getLocalEmployee']) {
                            context.dispatch('getScreenlockTasks')
                        }

                        // update free labelling
                        if (response.data.config && response.data.config.freeLabelling) {
                            context.commit('setFreeLabelling', response.data.config.freeLabelling, { root: true })
                        } else {
                            context.commit('setFreeLabelling', 0, { root: true })
                        }

                        if (response.data.config && response.data.config.pastLabelling) {
                            context.commit('setPastLabelling', response.data.config.pastLabelling, { root: true })
                        } else {
                            context.commit('setPastLabelling', 0, { root: true })
                        }

                        const sensorLimitation = response.data.config && typeof response.data.config.sensorLimitation !== 'undefined' && response.data.config.sensorLimitation ? response.data.config.sensorLimitation : 0
                        context.commit('setSensorLimitation', sensorLimitation, { root: true })

                        const dateLimitation = response.data.config && typeof response.data.config.dateLimitation !== 'undefined' && response.data.config.dateLimitation ? response.data.config.dateLimitation : 0
                        context.commit('setDateLimitation', dateLimitation, { root: true })

                        const relatedLabels = response.data.config && typeof response.data.config.relatedLabels !== 'undefined' && response.data.config.relatedLabels ? response.data.config.relatedLabels : 0
                        context.commit('setRelatedLabels', relatedLabels, { root: true })

                        const canCreateUsers = response.data.config && typeof response.data.config.canCreateUsers !== 'undefined' ? response.data.config.canCreateUsers : 1
                        context.commit('setCanCreateUsers', canCreateUsers, { root: true })

                        const canAccessTemplates = response.data.config && typeof response.data.config.canAccessTemplates !== 'undefined' ? response.data.config.canAccessTemplates : 1
                        context.commit('setCanAccessTemplates', canAccessTemplates, { root: true })

                        const maxLabelsPrint = response.data.config && typeof response.data.config.maxLabelsPrint !== 'undefined' && response.data.config.maxLabelsPrint ? response.data.config.maxLabelsPrint : 100
                        context.commit('setMaxLabelsPrint', maxLabelsPrint, { root: true })

                        const callLoginUsers = response.data.config && typeof response.data.config.callLoginUsers !== 'undefined' ? response.data.config.callLoginUsers : 0
                        context.commit('setCallLoginUsers', callLoginUsers, { root: true })

                        const timeout = response.data.config && typeof response.data.config.timeout !== 'undefined' && response.data.config.timeout ? parseInt(response.data.config.timeout) * 60 : idle_time
                        context.commit('setTimeout', timeout, { root: true })

                        const license_end_date = response.data && typeof response.data.license_end_date !== 'undefined' && response.data.license_end_date ? response.data.license_end_date : false
                        context.commit('setLicenseEndDate', license_end_date, { root: true })

                        const note_img = response.data.config && typeof response.data.config.note_img !== 'undefined' && response.data.config.note_img ? response.data.config.note_img : 'all'
                        context.commit('setNoteImg', note_img, { root: true })

                        const screenlockFlag = response.data.config && typeof response.data.config.screenlock !== 'undefined' ? response.data.config.screenlock : 0
                        context.commit('setScreenlockFlag', screenlockFlag, { root: true })

                        const offline = response.data.offline ? response.data.offline : 0
                        context.commit('setOffline', offline, { root: true })

                        const tp_round = response.data.config && response.data.config.tp_round && parseInt(response.data.config.tp_round) != 0 ? parseInt(response.data.config.tp_round) : false
                        context.commit('setTpRound', tp_round, { root: true })

                        const channels = response.data.config && response.data.config.channels && parseInt(response.data.config.channels) != 0 ? parseInt(response.data.config.channels) : false
                        context.commit('setLocationChannels', channels, { root: true })

                        const canReprintLabel = response.data.config && typeof response.data.config.canReprintLabel !== 'undefined' && parseInt(response.data.config.canReprintLabel) >= 0 ? response.data.config.canReprintLabel : 0
                        context.commit('setCanReprintLabel', canReprintLabel, { root: true })

                        const sensorBT = response.data.config && typeof response.data.config.sensor_bt !== 'undefined' && response.data.config.sensor_bt ? response.data.config.sensor_bt : false
                        context.commit('setSensorBluetoothConnection', sensorBT, { root: true })

                        const sentryLogs = response.data.config && typeof response.data.config.SENTRY_LOGS !== 'undefined' ? response.data.config.SENTRY_LOGS : false
                        context.commit('setSentryLogs', sentryLogs, { root: true })

                        const popupTextInput = response.data.config && typeof response.data.config.popupTextInput !== 'undefined' ? response.data.config.popupTextInput : false
                        context.commit('setPopupTextInput', popupTextInput, { root: true })

                        const popupTextInputHeight = response.data.config && typeof response.data.config.popupTextInputHeight !== 'undefined' ? response.data.config.popupTextInputHeight : 600
                        context.commit('setPopupTextInputHeight', popupTextInputHeight, { root: true })

                        const intlCurrency = response.data.config && typeof response.data.config.INTL_CURRENCY !== 'undefined' ? response.data.config.INTL_CURRENCY : 'EUR'
                        context.commit('intl/setCurrency', intlCurrency, { root: true })
                        const intlLocale = response.data.config && typeof response.data.config.INTL_LOCALE !== 'undefined' ? response.data.config.INTL_LOCALE : 'es-ES'
                        context.commit('intl/setLocale', intlLocale, { root: true })

                        context.dispatch('extractConfigOptions', response.data.config)

                        context.commit(
                            'loginUser/setLocation',
                            {
                                id: response.data.id,
                                name: response.data.name,
                                account_name: response.data.account_name,
                                avatar: response.data.avatar,
                                identification: response.data.identification,
                                username: response.data.username,
                                timezone: response.data.timezone,
                                email: response.data.email,
                                token: response.data.token,
                                city: response.data.city,
                                state: response.data.state,
                                address: response.data.address,
                                cp: response.data.cp
                            },
                            { root: true }
                        )
                        context.commit('conditionsVersion', response.data.conditions_version)

                        context.commit('setReleases', response.data.releases)

                        context.dispatch('loadTimeZonesAndLanguages', {})

                        context.commit('login', response)

                        const location = {
                            id: response.data.id,
                            name: response.data.name,
                            avatar: response.data.avatar,
                            identification: response.data.identification,
                            username: response.data.username,
                            timezone: response.data.timezone,
                            email: response.data.email,
                            token: response.data.token
                        }

                        await db_login.setItem('token', response.data.token)
                        await db_login.setItem('location', location)
                        await db_login.setItem('removeRolesLevel', response.data.removeRolesLevel)
                        await db_login.setItem('account', response.data.account_id)
                        const employeeId = context.rootGetters['loginUser/getLocalEmployee']
                        if (employeeId) {
                            await db_login.setItem(`employee_config_${employeeId}`, response.data.config)
                        } else {
                            await db_login.setItem('config', response.data.config)
                        }
                        // await db_login.setItem('printer', dt.printer)
                        await db_login.setItem('app_android', response.data.app_android)
                        await db_login.setItem('app_icon', response.data.app_icon)
                        await db_login.setItem('alex_info_url', response.data.alex_info_url)
                        await db_login.setItem('roles', response.data.roles)
                        await db_login.setItem('conditions_version', response.data.conditions_version)

                        try {
                            let location_info = await db_locations.getItem(response.data.username.toLowerCase())

                            await db_locations.setItem(response.data.username.toLowerCase(), {
                                account_name: response.data.account_name,
                                account_logo: response.data.avatar,
                                location_name: response.data.name,
                                password: window.btoa(response.data.passwd),
                                city: response.data.city,
                                state: response.data.state,
                                address: response.data.address,
                                cp: response.data.cp,
                                lang: response.data.lang
                            })
                        } catch (error) {
                            logError('ERROR SETTING LOCATION IN INIT', error)
                            await db_locations.setItem(response.data.username.toLowerCase(), {
                                account_name: response.data.account_name,
                                account_logo: response.data.avatar,
                                location_name: response.data.name,
                                password: window.btoa(response.data.passwd),
                                city: response.data.city,
                                state: response.data.state,
                                address: response.data.address,
                                cp: response.data.cp,
                                lang: response.data.lang
                            })
                        }
                    } else {
                        context.commit('setWrongIp', false)
                        context.commit('setNpsBlocked', false)

                        return context.commit('setLogin')
                    }
                })
                .catch((err) => {
                    console.error('403', err)
                    context.commit('setNpsBlocked', false)
                    return context.commit('setLogin')
                })
        } else {
            let location_info = await db_locations.getItem(state.location.username)
            context.commit(
                'loginUser/setLocation',
                {
                    id: state.location.id,
                    name: state.location.name,
                    account_name: location_info.account_name,
                    avatar: state.location.avatar,
                    identification: state.location.identification,
                    username: state.location.username,
                    timezone: state.location.timezone,
                    email: state.location.email,
                    token: state.location.token,
                    city: location_info.city,
                    state: location_info.state,
                    address: location_info.address,
                    cp: location_info.cp
                },
                { root: true }
            )
            const offlineLang = (location_info.lang || 'es_ES').split('_')[0]
            store.commit('changeLanguage', offlineLang)

            // Check if the timezone difference and device time are different
            context.dispatch('checkDateTimezoneDifference', { root: true })

            return context.commit('setLogin')
        }
    },

    async initAuditor(context) {
        await initLocalForage()

        // En initAuditor no interesa que se desloguee el usuario si no hay actividad

        if (fnCheckConnection()) {
            // Mirar si hay wrong_ip
            // Comprobar contraseñas
            return context.commit('setLogin')
        } else {
            /*  let locations = []
            let keys = await db_locations_auditor.keys()
            for (let key of keys) {
                let location = await db_locations_auditor.getItem(key)
                locations.push(location)
            }

            context.commit('loginUser/setLocations', locations, { root: true }) */
            const auditorInfo = store.getters['loginUser/getAuditor']

            const auditorLang = (auditorInfo.lang || 'es_ES').split('_')[0]
            store.commit('changeLanguage', auditorLang)
            return context.commit('setLogin')
        }
    },

    loadData(context, data) {
        if (data.lang) {
            const lang = data.lang.split('_')[0]
            store.commit('changeLanguage', lang)
        }

        if (data.reason_postpone) {
            let rsPostpone = data.reason_postpone
            rsPostpone['0'] = {
                id: '0',
                name: 'answer.postpone.default_reason'
            }
            store.commit('setReasonPostpone', rsPostpone)
        }
        if (data.reason_justify) {
            let rsJustify = data.reason_justify
            rsJustify['0'] = {
                id: '0',
                name: 'answer.justify.default_reason'
            }
            store.commit('setReasonJustify', rsJustify)
        }

        // Set states of issues
        if (data.issue_states) {
            let stIssues = data.issue_states
            for (let issueId in stIssues) {
                stIssues[issueId].default = stIssues[issueId].type == 1 ? 1 : 0
            }

            store.commit('setIssueStates', stIssues)
        }

        // Set action plan statuses available for the account
        if (data.action_plan_states) {
            const actionPlanStatusesArr = Object.values(data.action_plan_states)
            store.commit('actionplan/setActionPlanStatuses', actionPlanStatusesArr)
            db_login.setItem('action_plan_states', actionPlanStatusesArr)
        }
    },

    async logout(context, data) {
        const account = await db_login.getItem('account')
        const auditor = await db_login.getItem('auditorInfo')
        const location = await db_login.getItem('location')
        const config = context.getters.getConfig

        const handleSsoLogoutRedirect = () => {
            return finishWithSsoLogoutRedirect(config, {
                apiUrl: process.env.VUE_APP_API_URL,
                isApp: context.rootGetters.getIsApp
            })
        }

        if (fnCheckConnection()) {
            return api
                .login('logout', {
                    location_id: location ? location.id : null,
                    employee_id: auditor ? auditor.employee_id : null
                })
                .then(async function (response) {
                    if (response.status) {
                        await db_login.removeItem('token')
                        context.dispatch('loadData', response.data)
                        context.commit('logout')
                        const redirectResult = handleSsoLogoutRedirect()
                        return redirectResult.ssoRedirect ? redirectResult : true
                    }

                    return false
                })
                .catch((err) => {
                    logError(err)
                    context.commit('logout')
                    return handleSsoLogoutRedirect()
                })
        }

        context.commit('logout')
        return handleSsoLogoutRedirect()
    },

    async logoutAndGoToLogin(context, data) {
        const result = await context.dispatch('logout', data)
        await navigateToLoginAfterLogout(router, result)
        return result
    },

    async logoutClearAndGoToLogin(context, data) {
        const result = await context.dispatch('logout', data)
        await context.dispatch('clearLoginData')
        await navigateToLoginAfterLogout(router, result)
        return result
    },

    async logoutAuditorLocal(context) {
        context.commit('setIsLoadedLocation', false)
        // await db_locations.clear()
        await db_login.removeItem('location')
        state.location = {}
    },

    async clearLoginData(context) {
        await cleanAllStorage()
        // await db_locations.clear()
        // Set auditor false and isLoadedLocation false
        context.commit('setAuditorLogin', false)
        context.commit('setIsLoadedLocation', false)
        context.commit('loginUser/setAuditor', {}, { root: true })
        context.commit('logout')
    },

    async loadDashboardAccess(context, params) {
        const appVersion = process.env.VUE_APP_VERSION
        const platform = process.env.VUE_APP_IS_APP == 'TRUE' ? 1 : 2

        Vue.set(state, 'loadLocalForage', false)

        await cleanAllStorage()
        let url = 'initdbpreview/' + params.location + '/' + params.id
        if(parseInt(params.isrbe) == 1) {
            url += '?c=1'
        }

        return api
            .login(url, {
                expiration: params.expiration,
                version: appVersion,
                platform: platform
            })
            .then(async function (response) {
                if (response && !response.status) {
                    router.push('/login')
                } else if (response.status) {
                    if (typeof response.data.current_time !== 'undefined' && response.data.current_time) {
                        const currentTime = moment(moment(response.data.current_time).format('YYYY-MM-DD HH:mm:ss'))
                        const now = moment(moment().format('YYYY-MM-DD HH:mm:ss'))
                        const difference = Math.round(currentTime.diff(now, 'minutes') / 60) * 60
                        context.commit('setServerTimeDifference', difference, { root: true })
                    }

                    context.commit('setTimeZoneName', response.data.timezone_name, { root: true })

                    context.commit('setVersionCurrent', response.data.version_current, { root: true })
                    context.commit('setVersionMin', response.data.version_min, { root: true })
                    context.commit('setVersionRecommended', response.data.version_recommended, { root: true })
                    context.commit('setChatActive', response.data.chat, { root: true })

                    // KITCHEN MODE FEATURE FLAG
                    if (response.data && response.data.config && response.data.config.kitchen_mode) {
                        context.commit('setKitchenModeFeatureFlag', response.data.config.kitchen_mode)
                    }

                    context.dispatch('loadData', response.data)
                    context.dispatch('loadAppIcon', { path: response.data.app_icon, lastUpdate: response.data.app_icon_last_update * 1000 }, { root: true })
                    context.dispatch('loadExternalTools', response.data.menu_items, { root: true })
                    //   context.commit('employee/setRoles',response.data.roles,{root: true})
                    var printer = Object.assign(response.data.printer)
                    // context.commit('printer/setPrinterData', printer, { root: true })
                    context.dispatch('printer/savePrinterData', printer, { root: true })

                    if (response.data.config && response.data.config.home) {
                        context.commit('setCustomHome', response.data.config.home, { root: true })
                    } else {
                        context.commit('setCustomHome', false, { root: true })
                    }

                    // free labelling
                    const freeLabelling = response.data.config && typeof response.data.config.freeLabelling !== 'undefined' && response.data.config.freeLabelling ? 1 : 0
                    context.commit('setFreeLabelling', freeLabelling, { root: true })

                    const pastLabelling = response.data.config && typeof response.data.config.pastLabelling !== 'undefined' && response.data.config.pastLabelling ? 1 : 0
                    context.commit('setPastLabelling', pastLabelling, { root: true })

                    const sensorLimitation = response.data.config && typeof response.data.config.sensorLimitation !== 'undefined' && response.data.config.sensorLimitation ? response.data.config.sensorLimitation : 0
                    context.commit('setSensorLimitation', sensorLimitation, { root: true })

                    const relatedLabels = response.data.config && typeof response.data.config.relatedLabels !== 'undefined' && response.data.config.relatedLabels ? response.data.config.relatedLabels : 0
                    context.commit('setRelatedLabels', relatedLabels, { root: true })

                    const dateLimitation = response.data.config && typeof response.data.config.dateLimitation !== 'undefined' && response.data.config.dateLimitation ? response.data.config.dateLimitation : 0
                    context.commit('setDateLimitation', dateLimitation, { root: true })

                    const canCreateUsers = response.data.config && typeof response.data.config.canCreateUsers !== 'undefined' ? response.data.config.canCreateUsers : 1
                    context.commit('setCanCreateUsers', canCreateUsers, { root: true })

                    const canAccessTemplates = response.data.config && typeof response.data.config.canAccessTemplates !== 'undefined' ? response.data.config.canAccessTemplates : 1
                    context.commit('setCanAccessTemplates', canAccessTemplates, { root: true })

                    const maxLabelsPrint = response.data.config && typeof response.data.config.maxLabelsPrint !== 'undefined' && response.data.config.maxLabelsPrint ? response.data.config.maxLabelsPrint : 100
                    context.commit('setMaxLabelsPrint', maxLabelsPrint, { root: true })

                    const callLoginUsers = response.data.config && typeof response.data.config.callLoginUsers !== 'undefined' ? response.data.config.callLoginUsers : 0
                    context.commit('setCallLoginUsers', callLoginUsers, { root: true })

                    const timeout = response.data.config && typeof response.data.config.timeout !== 'undefined' && response.data.config.timeout ? parseInt(response.data.config.timeout) * 60 : idle_time
                    context.commit('setTimeout', timeout, { root: true })

                    const license_end_date = response.data && typeof response.data.license_end_date !== 'undefined' && response.data.license_end_date ? response.data.license_end_date : false

                    context.commit('setLicenseEndDate', license_end_date, { root: true })

                    const note_img = response.data.config && typeof response.data.config.note_img !== 'undefined' && response.data.config.note_img ? response.data.config.note_img : 'all'
                    context.commit('setNoteImg', note_img, { root: true })

                    const screenlockFlag = response.data.config && typeof response.data.config.screenlock !== 'undefined' ? response.data.config.screenlock : 0
                    context.commit('setScreenlockFlag', screenlockFlag, { root: true })

                    const offline = response.data.offline ? response.data.offline : 0
                    context.commit('setOffline', offline, { root: true })

                    const tp_round = response.data.config && response.data.config.tp_round && parseInt(response.data.config.tp_round) != 0 ? parseInt(response.data.config.tp_round) : false
                    context.commit('setTpRound', tp_round, { root: true })

                    const channels = response.data.config && response.data.config.channels && parseInt(response.data.config.channels) != 0 ? parseInt(response.data.config.channels) : false
                    context.commit('setLocationChannels', channels, { root: true })

                    const canReprintLabel = response.data.config && typeof response.data.config.canReprintLabel !== 'undefined' && parseInt(response.data.config.canReprintLabel) >= 0 ? response.data.config.canReprintLabel : 1
                    context.commit('setCanReprintLabel', canReprintLabel, { root: true })

                    const sensorBT = response.data.config && typeof response.data.config.sensor_bt !== 'undefined' && response.data.config.sensor_bt ? response.data.config.sensor_bt : false
                    context.commit('setSensorBluetoothConnection', sensorBT, { root: true })

                    const sentryLogs = response.data.config && typeof response.data.config.SENTRY_LOGS !== 'undefined' ? response.data.config.SENTRY_LOGS : false
                    context.commit('setSentryLogs', sentryLogs, { root: true })

                    const popupTextInput = response.data.config && typeof response.data.config.popupTextInput !== 'undefined' ? response.data.config.popupTextInput : false
                    context.commit('setPopupTextInput', popupTextInput, { root: true })

                    const popupTextInputHeight = response.data.config && typeof response.data.config.popupTextInputHeight !== 'undefined' ? response.data.config.popupTextInputHeight : 600
                    context.commit('setPopupTextInputHeight', popupTextInputHeight, { root: true })

                    const intlCurrency = response.data.config && typeof response.data.config.INTL_CURRENCY !== 'undefined' ? response.data.config.INTL_CURRENCY : 'EUR'
                    context.commit('intl/setCurrency', intlCurrency, { root: true })
                    const intlLocale = response.data.config && typeof response.data.config.INTL_LOCALE !== 'undefined' ? response.data.config.INTL_LOCALE : 'es-ES'
                    context.commit('intl/setLocale', intlLocale, { root: true })

                    context.dispatch('extractConfigOptions', response.data.config)

                    context.commit(
                        'loginUser/setLocation',
                        {
                            id: response.data.id,
                            name: response.data.name,
                            account_name: response.data.account_name,
                            avatar: response.data.avatar,
                            identification: response.data.identification,
                            username: response.data.username,
                            timezone: response.data.timezone,
                            email: response.data.email,
                            token: response.data.token,
                            city: response.data.city,
                            state: response.data.state,
                            address: response.data.address,
                            cp: response.data.cp
                        },
                        { root: true }
                    )
                    context.commit('loginUser/setAccount', response.data.account_id, { root: true })

                    context.commit('conditionsVersion', response.data.conditions_version)
                    context.commit('login', response)

                    const location = {
                        id: response.data.id,
                        name: response.data.name,
                        avatar: response.data.avatar,
                        identification: response.data.identification,
                        username: response.data.username,
                        timezone: response.data.timezone,
                        email: response.data.email,
                        token: response.data.token
                    }

                    await db_login.setItem('token', response.data.token)
                    await db_login.setItem('location', location)
                    await db_login.setItem('removeRolesLevel', response.data.removeRolesLevel)
                    await db_login.setItem('account', response.data.account_id)
                    await db_login.setItem('config', response.data.config)
                    // await db_login.setItem('printer', dt.printer)
                    await db_login.setItem('app_android', response.data.app_android)
                    await db_login.setItem('app_icon', response.data.app_icon)
                    await db_login.setItem('alex_info_url', response.data.alex_info_url)
                    await db_login.setItem('roles', response.data.roles)
                    await db_login.setItem('conditions_version', response.data.conditions_version)

                    var loginUser = {}
                    loginUser.status = true
                    loginUser.emp_id = response.data.employee.id
                    loginUser.token = response.data.token
                    loginUser.user = response.data.employee
                    var avatar = loginUser.user.avatar
                    if (!avatar.includes('//')) {
                        loginUser.user.avatar = css_image_url + 'avatar/' + loginUser.user.avatar
                    }
                    loginUser.user.image = 'url(' + loginUser.user.avatar + ')'

                    context.commit('setToken', response.data.token)
                    context.dispatch('loadTimeZonesAndLanguages', {})

                    context.dispatch('applyTrialFromApi', response.data)
                    context.commit('setTrialMessage', response.data.trial_message)
                    context.commit('setLoginFromCP', true, { root: true })

                    debug = 1
                    localStorage.setItem('debug', 1)

                    return context.dispatch('employee/loadEmployees', {}, { root: true }).then(function () {
                        return context.dispatch('employee/loadRoles', {}, { root: true }).then(function () {
                            context.commit('employee/addEmployee', loginUser.user, { root: true })
                            context.commit('loginUser/login', loginUser, { root: true })
                            return context.commit('setisLoaded', true)
                        })
                    })
                }
                return true
            })
            .catch((error) => {
                logError(error)
            })
    },
    async getScreenlockTasks(context) {
        context.commit('setScreenlockData', { checklist_id: false, message: '' })
        if (!fnCheckConnection()) {
            let screenlock = await db_screenlock.getItem('screenlock')
            if (screenlock) {
                screenlock = JSON.parse(screenlock)
                context.commit('setScreenlockData', screenlock.screenlock)

                if (screenlock.data) {
                    _.forEach(_.values(screenlock.data), async function (value, key) {
                        if (value.id !== 'undefined' && value !== 'undefined') {
                            await db_tasks.setItem(value.id, value)
                        }
                    })

                    return context.dispatch('tasks/mergeOffline', { data: screenlock.data }, { root: true }).then(function (response2) {
                        return context.dispatch('tasks/format', { data: response2, screenlock: true }, { root: true }).then(() => {
                            return context.dispatch('tasks/calcScore', { checklistId: screenlock.checklist_id, itemId: undefined }, { root: true })
                        })
                    })
                }
            }
            return
        } else {
            db_screenlock.clear().then(async () => {
                await db_screenlock.setItem('screenlock', JSON.stringify({ checklist_id: false, message: '' }))
            })
        }

        return api.get('checklist/screenlock').then(function (response) {
            context.commit('setScreenlockData', response.screenlock)

            db_screenlock.clear().then(async () => {
                await db_screenlock.setItem('screenlock', JSON.stringify(response))
            })

            if (response.data) {
                _.forEach(_.values(response.data), async function (value, key) {
                    if (value.id !== 'undefined' && value !== 'undefined') {
                        await db_tasks.setItem(value.id, value)
                    }
                })

                return context.dispatch('tasks/mergeOffline', { data: response.data }, { root: true }).then(function (response2) {
                    return context.dispatch('tasks/format', { data: response2, screenlock: true }, { root: true }).then(() => {
                        return context.dispatch('tasks/calcScore', { checklistId: response.screenlock.checklist_id, itemId: undefined }, { root: true })
                    })
                })
            }
        })
    },
    getStoredSites(context, params) {
        let storedLocations = []
        return db_locations
            .iterate((value, key, iterationNumber) => {
                value.username = key
                storedLocations.push(value)
            })
            .then(() => {
                return storedLocations
            })
    },
    removeStoredSite(context, params) {
        return db_locations.removeItem(params.username).then(() => {
            return true
        })
    },

    // RECOVER PASSWORD PROCCESS
    sendForgotMail(context, params) {
        return api.post('location/password/recover', { identification: params.identification }).then(function (response) {
            if (response) {
                return response
            }
        })
    },
    sendCode(context, params) {
        return api
            .post('location/password/recover/validate/code', {
                location_id: params.location_id,
                code: params.code,
                identification: params.identification
            })
            .then(function (response) {
                if (response) {
                    return response
                }
            })
    },
    resendCode(context, params) {
        return api.post('location/code/resend', { identification: params.identification }).then(function (response) {
            if (response) {
                return response
            }
        })
    },
    resetPassword(context, params) {
        return api
            .post('location/password/new', {
                location_id: params.location_id,
                password: params.password,
                hashPassword: params.hashPassword,
                identification: params.identification
            })
            .then(async function (response) {
                if (response.status) {
                    try {
                        await db_locations.setItem(params.username.toLowerCase(), {
                            account_name: response.data.account_name,
                            account_logo: response.data.avatar,
                            location_name: response.data.name,
                            password: window.btoa(params.password),
                            city: response.data.city,
                            state: response.data.state,
                            address: response.data.address,
                            cp: response.data.cp,
                            lang: response.data.lang
                        })
                    } catch (error) {
                        console.error('ERROR SETTING LOCATION', error)
                    }

                    // KITCHEN MODE FEATURE FLAG
                    if (response.data && response.data.config && response.data.config.kitchen_mode) {
                        context.commit('setKitchenModeFeatureFlag', response.data.config.kitchen_mode)
                    }

                    if (typeof response.data.current_time !== 'undefined' && response.data.current_time) {
                        const currentTime = moment(moment(response.data.current_time).format('YYYY-MM-DD HH:mm:ss'))
                        const now = moment(moment().format('YYYY-MM-DD HH:mm:ss'))
                        const difference = Math.round(currentTime.diff(now, 'minutes') / 60) * 60
                        context.commit('setServerTimeDifference', difference, { root: true })
                    }

                    context.commit('setTimeZoneName', response.data.timezone_name, { root: true })

                    context.commit('setVersionCurrent', response.data.version_current, { root: true })
                    context.commit('setVersionMin', response.data.version_min, { root: true })
                    context.commit('setVersionRecommended', response.data.version_recommended, { root: true })

                    //   store.dispatch("checkChangesPrint", response.data.printer);
                    context.dispatch('loadData', response.data)
                    context.dispatch('loadAppIcon', { path: response.data.app_icon, lastUpdate: response.data.app_icon_last_update * 1000 }, { root: true })
                    context.dispatch('loadExternalTools', response.data.menu_items, { root: true })
                    context.dispatch('pusher/init', {}, { root: true })
                    var printer = Object.assign(response.data.printer)
                    // context.commit('printer/setPrinterData', printer, { root: true })
                    context.dispatch('printer/savePrinterData', printer, { root: true })

                    if (response.data.config && response.data.config.home) {
                        context.commit('setCustomHome', response.data.config.home, { root: true })
                    } else {
                        context.commit('setCustomHome', false, { root: true })
                    }

                    // free labelling
                    const freeLabelling = response.data.config && typeof response.data.config.freeLabelling !== 'undefined' && response.data.config.freeLabelling ? 1 : 0
                    context.commit('setFreeLabelling', freeLabelling, { root: true })

                    const pastLabelling = response.data.config && typeof response.data.config.pastLabelling !== 'undefined' && response.data.config.pastLabelling ? 1 : 0
                    context.commit('setPastLabelling', pastLabelling, { root: true })

                    const sensorLimitation = response.data.config && typeof response.data.config.sensorLimitation !== 'undefined' && response.data.config.sensorLimitation ? response.data.config.sensorLimitation : 0
                    context.commit('setSensorLimitation', sensorLimitation, { root: true })

                    const relatedLabels = response.data.config && typeof response.data.config.relatedLabels !== 'undefined' && response.data.config.relatedLabels ? response.data.config.relatedLabels : 0
                    context.commit('setRelatedLabels', relatedLabels, { root: true })

                    const dateLimitation = response.data.config && typeof response.data.config.dateLimitation !== 'undefined' && response.data.config.dateLimitation ? response.data.config.dateLimitation : 0
                    context.commit('setDateLimitation', dateLimitation, { root: true })

                    const canCreateUsers = response.data.config && typeof response.data.config.canCreateUsers !== 'undefined' ? response.data.config.canCreateUsers : 1
                    context.commit('setCanCreateUsers', canCreateUsers, { root: true })

                    const canAccessTemplates = response.data.config && typeof response.data.config.canAccessTemplates !== 'undefined' ? response.data.config.canAccessTemplates : 1
                    context.commit('setCanAccessTemplates', canAccessTemplates, { root: true })

                    const maxLabelsPrint = response.data.config && typeof response.data.config.maxLabelsPrint !== 'undefined' && response.data.config.maxLabelsPrint ? response.data.config.maxLabelsPrint : 100
                    context.commit('setMaxLabelsPrint', maxLabelsPrint, { root: true })

                    const callLoginUsers = response.data.config && typeof response.data.config.callLoginUsers !== 'undefined' ? response.data.config.callLoginUsers : 0
                    context.commit('setCallLoginUsers', callLoginUsers, { root: true })

                    const timeout = response.data.config && typeof response.data.config.timeout !== 'undefined' && response.data.config.timeout ? parseInt(response.data.config.timeout) * 60 : idle_time
                    context.commit('setTimeout', timeout, { root: true })

                    const license_end_date = response.data && typeof response.data.license_end_date !== 'undefined' && response.data.license_end_date ? response.data.license_end_date : false

                    context.commit('setLicenseEndDate', license_end_date, { root: true })

                    const note_img = response.data.config && typeof response.data.config.note_img !== 'undefined' && response.data.config.note_img ? response.data.config.note_img : 'all'
                    context.commit('setNoteImg', note_img, { root: true })

                    const screenlockFlag = response.data.config && typeof response.data.config.screenlock !== 'undefined' ? response.data.config.screenlock : 0
                    context.commit('setScreenlockFlag', screenlockFlag, { root: true })

                    const offline = response.data.offline ? response.data.offline : 0
                    context.commit('setOffline', offline, { root: true })

                    const tp_round = response.data.config && response.data.config.tp_round && parseInt(response.data.config.tp_round) != 0 ? parseInt(response.data.config.tp_round) : false
                    context.commit('setTpRound', tp_round, { root: true })

                    const channels = response.data.config && response.data.config.channels && parseInt(response.data.config.channels) != 0 ? parseInt(response.data.config.channels) : false
                    context.commit('setLocationChannels', channels, { root: true })

                    const canReprintLabel = response.data.config && typeof response.data.config.canReprintLabel !== 'undefined' && parseInt(response.data.config.canReprintLabel) >= 0 ? response.data.config.canReprintLabel : 1
                    context.commit('setCanReprintLabel', canReprintLabel, { root: true })

                    const sensorBT = response.data.config && typeof response.data.config.sensor_bt !== 'undefined' && response.data.config.sensor_bt ? response.data.config.sensor_bt : false
                    context.commit('setSensorBluetoothConnection', sensorBT, { root: true })

                    const sentryLogs = response.data.config && typeof response.data.config.SENTRY_LOGS !== 'undefined' ? response.data.config.SENTRY_LOGS : false
                    context.commit('setSentryLogs', sentryLogs, { root: true })

                    const popupTextInput = response.data.config && typeof response.data.config.popupTextInput !== 'undefined' ? response.data.config.popupTextInput : false
                    context.commit('setPopupTextInput', popupTextInput, { root: true })

                    const popupTextInputHeight = response.data.config && typeof response.data.config.popupTextInputHeight !== 'undefined' ? response.data.config.popupTextInputHeight : 600
                    context.commit('setPopupTextInputHeight', popupTextInputHeight, { root: true })

                    const intlCurrency = response.data.config && typeof response.data.config.INTL_CURRENCY !== 'undefined' ? response.data.config.INTL_CURRENCY : 'EUR'
                    context.commit('intl/setCurrency', intlCurrency, { root: true })
                    const intlLocale = response.data.config && typeof response.data.config.INTL_LOCALE !== 'undefined' ? response.data.config.INTL_LOCALE : 'es-ES'
                    context.commit('intl/setLocale', intlLocale, { root: true })

                    context.dispatch('extractConfigOptions', response.data.config)

                    context.commit(
                        'loginUser/setLocation',
                        {
                            account_name: response.data.account_name,
                            id: response.data.id,
                            name: response.data.name,
                            avatar: response.data.avatar,
                            identification: response.data.identification,
                            username: response.data.username,
                            timezone: response.data.timezone,
                            email: response.data.email,
                            token: response.data.token,
                            city: response.data.city,
                            state: response.data.state,
                            address: response.data.address,
                            cp: response.data.cp
                        },
                        { root: true }
                    )

                    context.commit('loginUser/setAccount', response.data.account_id, { root: true })

                    context.commit('setReleases', response.data.releases)

                    context.commit('login', response)

                    const location = {
                        id: response.data.id,
                        name: response.data.name,
                        avatar: response.data.avatar,
                        identification: response.data.identification,
                        username: response.data.username,
                        timezone: response.data.timezone,
                        email: response.data.email,
                        token: response.data.token
                    }

                    await db_login.setItem('token', response.data.token)
                    await db_login.setItem('location', location)
                    await db_login.setItem('removeRolesLevel', response.data.removeRolesLevel)
                    await db_login.setItem('account', response.data.account_id)
                    await db_login.setItem('config', response.data.config)
                    // await db_login.setItem('printer', dt.printer)
                    await db_login.setItem('app_android', response.data.app_android)
                    await db_login.setItem('app_icon', response.data.app_icon)
                    await db_login.setItem('alex_info_url', response.data.alex_info_url)
                    await db_login.setItem('roles', response.data.roles)
                    await db_login.setItem('conditions_version', response.data.conditions_version)

                    context.dispatch('loadTimeZonesAndLanguages', {})

                    context.dispatch('applyTrialFromApi', response.data)
                    context.commit('setTrialMessage', response.data.trial_message)

                    context.commit('setWrongIp', false)
                    context.commit('setisLoaded', true)
                    context.commit('setIsLoadedLocation', true)

                    //   context.commit('employee/setRoles',response.data.roles,{root: true})
                    if (context.rootGetters.getIsAndroid && FirebasePlugin) {
                        FirebasePlugin.setUserId(response.data.id)
                        FirebasePlugin.setUserProperty('account', response.data.account_id)
                        FirebasePlugin.setUserProperty('location_name', response.data.location_name)
                        FirebasePlugin.setCrashlyticsUserId(response.data.id)
                    }
                } else {
                    //   overlay.hide()
                    return false
                }
            })
    },

    loadReleases(context, params) {
        const language = localStorage.getItem('language')
        let lang = ''
        switch (language) {
            case 'es':
                lang = 'es_ES'
                break
            case 'en':
                lang = 'en_US'
                break
            case 'pt':
                lang = 'pt_PT'
                break
            case 'de':
                lang = 'de_DE'
                break
            case 'it_IT':
                lang = 'it'
                break
        }
        return api
            .post('releases', { lang: lang })
            .then((response) => {
                context.commit('setAllReleases', response.data)
                return response
            })
            .catch((error) => {
                logError(error)
                return false
            })
    },

    async loadTimeZonesAndLanguages(context, params) {
        let languages = []
        await db_languages.iterate(function (value, key, iterationNumber) {
            languages.push(value)
        })
        
        let oldLanguages = []
        if (languages && languages.length > 0) {
            oldLanguages = languages.map(language => language.code)
        }
        const response = await api.get('account/information')
        if (!response.status) {
            return false
        }
        languages = response.data.languages
        
        let aLanguages = []
        for (let id in languages) {
            aLanguages.push(languages[id].code.split('_')[0])
            const language = languages[id]
            if (!oldLanguages.includes(language.code)) {
                await db_languages.setItem(id, language)
            }
        }
        context.commit('setLanguages', aLanguages, { root: true })
    },

    async cleanOfflineDatabase() {
        await cleanAllStorage()
    },

    applyTrialFromApi(context, data) {
        const { expirationDate, showTrialBanner } = normalizeTrialFromApi(data)
        context.commit('setTrialEndDate', expirationDate)
        context.commit('setShowTrialBanner', showTrialBanner)
        context.dispatch('calculateTrialDaysLeft')
    },

    calculateTrialDaysLeft(context) {
        const expirationDate = context.getters['getTrialEndDate']
        const showTrialBanner = context.getters['getShowTrialBanner']
        const serverTimeDifference = context.rootGetters['loginUser/getServerTimeDifference']
        const timezone = context.rootGetters['getTimeZoneName']
        let now = moment()

        context.commit('showTrialEndScreen', false)
        context.commit('setTrialDaysLeft', 0)

        if (showTrialBanner) {
            context.commit('showTrialEndScreen', true)
            return
        }

        if (typeof expirationDate === 'string' && expirationDate !== '') {
            try {
                const options = {
                    timeZone: timezone,
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: false
                }
                const copy = moment(now)

                const dateTimeFormat = new Intl.DateTimeFormat([], options).formatToParts()
                const objectDate = {}
                for (const item of dateTimeFormat) {
                    objectDate[item.type] = item.value
                }
                const dateString = `${objectDate['day'].padStart(2, '0')}/${objectDate['month'].padStart(2, '0')}/${objectDate['year']} ${objectDate['hour'].padStart(2, '0')}:${objectDate['minute'].padStart(2, '0')}`
                now = moment(dateString, 'DD/MM/YYYY HH:mm')

                if (now.diff(copy, 'minutes') != serverTimeDifference) {
                    const difference = serverTimeDifference - now.diff(copy, 'minutes')
                    now = now.add(difference, 'minutes')
                }

                now = moment(now, 'DD/MM/YYYY')
            } catch (error) {
                logError(error)
            }

            const trialEndMoment = moment(expirationDate)
            let differenceDays = trialEndMoment.diff(now, 'days') + 1
            let trial_days_left = now.isAfter(trialEndMoment) ? 0 : differenceDays

            context.commit('setTrialDaysLeft', trial_days_left)

            if (trialEndMoment.isSameOrBefore(now)) {
                context.commit('showTrialEndScreen', true)
                router.push('home')
            }
        }
    },
    emailForgot(context, params) {
        return api.post('admin/employee/rememberpassword', params).then(function (response) {
            if (response.status) {
                return response
            } else {
                return response
            }
        })
    },
    async extractConfigOptions(context, config) {
        context.dispatch('setOption', { config: config, keyname: 'SALES_FORECAST_TYPE', endpoint: 'production/setOption', defaultValue: 'MANUAL' })
        context.dispatch('setOption', { config: config, keyname: 'PRODUCTION_CONFIG', endpoint: 'production/setOption', defaultValue: {"preparation":{"visible":true}, "tempering":{"visible":true}, "defrost":{"visible":true}} })
        context.dispatch('setOption', { config: config, keyname: 'PLS_DEVICE_CONFIG', endpoint: 'production/setOption', defaultValue: {"units":{"min":2, "max":4}} })
    },
    async setOption(context, params) {
        let config = params.config
        let keyname = params.keyname
        let endpoint = params.endpoint
        let defaultValue = params.defaultValue
        const value = config && typeof config[keyname] !== 'undefined' ? config[keyname] : defaultValue
        context.commit(endpoint, { keyname, value }, { root: true })
    }
}

// mutations
const mutations = {
    async login(state, response) {
        if (response.status) {
            state.hasLogin = true

            var dt = response.data

            const location = {
                id: dt.id,
                name: dt.name,
                avatar: dt.avatar,
                identification: dt.identification,
                username: dt.username,
                timezone: dt.timezone,
                email: dt.email,
                token: dt.token
            }

            state.location = location
            state.removeRolesLevel = dt.removeRolesLevel
            state.account = dt.account_id
            state.config = dt.config
            state.radarFlag = dt.config && typeof dt.config.radar !== 'undefined' ? dt.config.radar : 0
            state.npsBlocked = dt.config && dt.config.nps_enabled === false
            state.printer = dt.printer
            state.token = dt.token
            refreshLanguageFromApi()
            state.config = dt.config
            state.app_android = dt.app_android
            state.conditions_version = dt.conditions_version
            state.roles = dt.roles

            // await db_login.setItem('token', dt.token)
            // await db_login.setItem('location', location)
            // await db_login.setItem('removeRolesLevel', dt.removeRolesLevel)
            // await db_login.setItem('account', dt.account_id)
            // await db_login.setItem('config', dt.config)
            // // await db_login.setItem('printer', dt.printer)
            // await db_login.setItem('app_android', dt.app_android)
            // await db_login.setItem('app_icon', dt.app_icon)
            // await db_login.setItem('alex_info_url', dt.alex_info_url)
            // await db_login.setItem('roles', dt.roles)
            // await db_login.setItem('conditions_version', dt.conditions_version)
        } else {
            state.hasLogin = false
        }

        // this._vm.$overlay.hide()
    },
    setLogin(state) {
        // const token = await db_login.getItem('token')
        // if (typeof token !== 'undefined') {
        //     state.hasLogin = true
        // }
        if (state.token != null && state.token != '') {
            state.hasLogin = true
        }
    },
    setIsLoadedLocation(state, loaded) {
        state.isLoadedLocation = loaded
        Vue.set(state, 'isLoadedLocation', loaded)
    },
    setAuditorLocalSelected(state, auditor) {
        state.auditorLocalSelected = auditor
        Vue.set(state, 'auditorLocalSelected', auditor)
    },
    logout(state) {
        store.dispatch('production/resetHomeWidgetData', null, { root: true })
        var language = localStorage.language
        const userPreferences = localStorage.getItem(USER_PREFERENCES_STORAGE_KEY)
        localStorage.clear()
        sessionStorage.removeItem(REFINER_SESSION_TRACKED_KEY)
        // db_login.clear()
        localStorage.setItem('language', language)
        if (userPreferences) {
            localStorage.setItem(USER_PREFERENCES_STORAGE_KEY, userPreferences)
        }

        // reset LOCAL
        state.hasLogin = false

        // this._vm.$overlay.hide()
    },
    async conditionsVersion(state, version) {
        state.conditionsVersion = version
        await db_login.setItem('conditions_version', version)
    },
    setisLoaded(state, loaded) {
        state.loadLocalForage = loaded
    },
    setReleases(state, data) {
        state.releases = data
        Vue.set(state, 'releases', data)
    },
    setAllReleases(state, data) {
        state.allReleases = data
        Vue.set(state, 'allReleases', data)
    },
    setBlacklistData(state, data) {
        const blackListData = {
            warning: data.warning,
            blocked: data.blocked
        }
        state.blackListData = blackListData
        Vue.set(state, 'blacklistData', blackListData)
    },
    setScreenlockData(state, data) {
        const screenlockData = {
            checklist_id: data.checklist_id,
            message: data.message
        }

        Vue.set(state, 'screenlock', screenlockData)
    },
    setWrongIp(state, data) {
        state.wrong_ip = data
        Vue.set(state, 'wrong_ip', data)
    },
    async setToken(state, data) {
        state.token = data
        Vue.set(state, 'token', data)
        return await db_login.setItem('token', data)
    },
    async setTrialEndDate(state, data) {
        const expirationDate = typeof data === 'string' ? data : ''
        state.trial_end_date = expirationDate
        await db_login.setItem('trial_end_date', expirationDate)
        Vue.set(state, 'trial_end_date', expirationDate)
    },
    async setShowTrialBanner(state, data) {
        state.show_trial_banner = Boolean(data)
        await db_login.setItem('show_trial_banner', state.show_trial_banner)
        Vue.set(state, 'show_trial_banner', state.show_trial_banner)
    },
    showTrialEndScreen(state, data) {
        state.showTrialEndScreen = data
        Vue.set(state, 'showTrialEndScreen', data)
    },
    setTrialDaysLeft(state, data) {
        state.trial_days_left = data
        Vue.set(state, 'trial_days_left', data)
    },
    setTrialMessage(state, data) {
        state.trial_message = data
        Vue.set(state, 'trial_message', data)
    },
    async setConfig(state, config) {
        state.config = config
        refreshLanguageFromApi()
        Vue.set(state, 'config', config)
        state.radarFlag = config && typeof config.radar !== 'undefined' ? config.radar : 0
        await db_login.setItem('config', config)
    },
    setKitchenModeFeatureFlag(state, value) {
        state.kitchenModeFeatureFlag = value
    },
    setRadarFlag(state, value) {
        state.radarFlag = value
    },
    setNpsBlocked(state, value) {
        state.npsBlocked = value === true
    }
}

async function initLocalForage(useCachedRadarFlag = true) {
    await localStorageToLocalForage()

    state.location = await db_login.getItem('location')
    state.removeRolesLevel = await db_login.getItem('removeRolesLevel')
    state.conditionsVersion = await db_login.getItem('conditions_version')
    state.token = await db_login.getItem('token')
    state.roles = await db_login.getItem('roles')
    state.app_android = await db_login.getItem('app_android')
    state.account = await db_login.getItem('account')
    state.printer = await db_login.getItem('printer')
    const locationConfig = await db_login.getItem('config')
    const employeeId = await db_login.getItem('employee_id')
    const employeeConfig = employeeId ? await db_login.getItem(`employee_config_${employeeId}`) : null
    state.config = employeeConfig || locationConfig
    state.radarFlag = useCachedRadarFlag && state.config && typeof state.config.radar !== 'undefined' ? state.config.radar : 0
    refreshLanguageFromApi()
    state.loadLocalForage = true
    const cachedExpirationDate = await db_login.getItem('trial_end_date')
    state.trial_end_date = typeof cachedExpirationDate === 'string' ? cachedExpirationDate : ''
    state.show_trial_banner = Boolean(await db_login.getItem('show_trial_banner'))
    state.auditor = await db_login.getItem('auditor')

    // Load action plan statuses from localForage
    const actionPlanStates = await db_login.getItem('action_plan_states')
    if (actionPlanStates) {
        store.commit('actionplan/setActionPlanStatuses', actionPlanStates)
    }
}

function refreshLanguageFromApi() {
    const language = localStorage.getItem('language')
    if (language && language !== 'init' && fnCheckConnection()) {
        i18n.loadLanguageAsync(language, true)
    }
}

async function localStorageToLocalForage() {
    const auditor = localStorage.auditor
    await db_login.setItem('auditor', auditor)

    const token = localStorage.token
    if (typeof token !== 'undefined') {
        await db_login.setItem('token', token)
        localStorage.clear()
    }
}

function cleanLocalForage() {
    // console.warn('cleanLocalForage')
    let promises = [db_issues.clear(), db_issues_templates.clear(), db_tasks.clear(), db_templates.clear(), db_timers.clear(), db_checks_to_sync.clear(), db_login.clear(), db_updates.clear(), db_employees.clear(), db_roles.clear(), db_categories.clear(), db_products.clear(), db_collections.clear(), db_printlogs.clear(), db_error_logs.clear(), db_library_categories.clear(), db_sections.clear(), db_files.clear(), db_highlight_files.clear(), db_logbook.clear(), db_legal_conditions.clear(), dbEmployeeLoginLogs.clear(), db_printlogs_android.clear(), db_languages.clear(), db_tasks_queue.clear(), registers_items.clear(), db_registers_queue.clear(), audits_items.clear(), db_audits_queue.clear(), issues_items.clear(), db_issues_queue.clear(), db_registers.clear(), db_screenlock.clear()]

    return Promise.all(promises)
        .then((response) => {
            return true
        })
        .catch((error) => {
            logError(error)
            return false
        })
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
